import { Helmet } from '../components/Helmet';
import { Link } from 'react-router-dom';
import { Clock, Users, BarChart, CheckCircle, Filter } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { coursesData, objectives, audiences, durations, formats, Course } from '../data/courses.ts'; // Adjust path as needed

const TOUS = "Tous" as const;

const FormationsPage = () => {
  const [activeObjective, setActiveObjective] = useState<string>(TOUS);
  const [activeAudience, setActiveAudience] = useState<string>(TOUS);
  const [activeDuration, setActiveDuration] = useState<string>(TOUS);
  const [activeFormat, setActiveFormat] = useState<string>(TOUS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const resetFilters = () => {
    setActiveObjective(TOUS);
    setActiveAudience(TOUS);
    setActiveDuration(TOUS);
    setActiveFormat(TOUS);
  };

  // Filter courses based on selected filters
  const filteredCourses: Course[] = coursesData.filter(course => {
    const objectiveMatch = activeObjective === TOUS || course.objectives.includes(activeObjective as typeof course.objectives[number]);
    const audienceMatch = activeAudience === TOUS || course.audiences.includes(activeAudience as typeof course.audiences[number]);
    const durationMatch = activeDuration === TOUS || course.durationOptions.includes(activeDuration as typeof course.durationOptions[number]);
    const formatMatch = activeFormat === TOUS || course.format === activeFormat;
    return objectiveMatch && audienceMatch && durationMatch && formatMatch;
  });

  return (
    <>
      <Helmet>
        <title>Catalogue de Formations</title>
        <meta
          name="description"
          content="Découvrez notre catalogue complet de formations en Intelligence Artificielle pour entreprises. Des formations pour tous les niveaux et tous les besoins."
        />
        <link rel="canonical" href="https://www.upnov.com/formation-ia-entreprise" />
        <meta property="og:title" content="Catalogue de Formations" />
        <meta property="og:description" content="Découvrez notre catalogue complet de formations en Intelligence Artificielle pour entreprises. Des formations pour tous les niveaux et tous les besoins." />
        <meta property="og:url" content="https://www.upnov.com/formation-ia-entreprise" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.upnov.com/surmesure.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-primary text-white py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-lg mb-6">Catalogue de formations</h1>
            <p className="text-xl mb-8">
              Découvrez nos formations en Intelligence Artificielle conçues pour répondre
              aux besoins spécifiques de votre entreprise, quel que soit votre secteur d'activité.
            </p>
            <a href="#formations" className="btn-accent">
              Parcourir les formations
            </a>
          </div>
        </div>
      </section>

      {/* Formations Section */}
      <section id="formations" className="section-padding bg-gray-50">
        <div className="container-custom">
          {/* Mobile Filter Toggle */}
          <div className="md:hidden mb-6">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="w-full flex items-center justify-between bg-white p-4 rounded-lg shadow-sm"
            >
              <span className="font-medium">Filtrer les formations</span>
              <Filter className="h-5 w-5" />
            </button>
          </div>

          <div className="flex flex-col md:flex-row gap-8">
            {/* Filters Sidebar */}
            <div
              className={`md:w-1/4 ${isFilterOpen ? 'block' : 'hidden'} md:block transition-all duration-300`}
            >
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Filtres</h2>
                {/* Objectif Filter */}
                <div className="mb-6">
                  <h3 className="font-bold text-gray-900 mb-3">Objectif</h3>
                  <div className="space-y-2">
                    {[TOUS, ...objectives].map(objective => (
                      <button
                        key={objective}
                        onClick={() => setActiveObjective(objective)}
                        className={`block w-full text-left py-2 px-3 rounded-lg transition-colors ${activeObjective === objective
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'hover:bg-gray-100'
                          }`}
                      >
                        {objective}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Public Filter */}
                <div className="mb-6">
                  <h3 className="font-bold text-gray-900 mb-3">Public</h3>
                  <div className="space-y-2">
                    {[TOUS, ...audiences].map(audience => (
                      <button
                        key={audience}
                        onClick={() => setActiveAudience(audience)}
                        className={`block w-full text-left py-2 px-3 rounded-lg transition-colors ${activeAudience === audience
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'hover:bg-gray-100'
                          }`}
                      >
                        {audience}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Durée Filter */}
                <div className="mb-6">
                  <h3 className="font-bold text-gray-900 mb-3">Durée</h3>
                  <div className="space-y-2">
                    {[TOUS, ...durations].map(duration => (
                      <button
                        key={duration}
                        onClick={() => setActiveDuration(duration)}
                        className={`block w-full text-left py-2 px-3 rounded-lg transition-colors ${activeDuration === duration
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'hover:bg-gray-100'
                          }`}
                      >
                        {duration}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Format Filter */}
                <div className="mb-6">
                  <h3 className="font-bold text-gray-900 mb-3">Format</h3>
                  <div className="space-y-2">
                    {[TOUS, ...formats].map(format => (
                      <button
                        key={format}
                        onClick={() => setActiveFormat(format)}
                        className={`block w-full text-left py-2 px-3 rounded-lg transition-colors ${activeFormat === format
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'hover:bg-gray-100'
                          }`}
                      >
                        {format}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Reset Filters */}
                <button
                  onClick={resetFilters}
                  className="text-primary font-medium hover:underline"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            </div>

            {/* Courses Grid */}
            <div className="md:w-3/4">
              <h2 className="text-2xl font-semibold mb-6">
                {filteredCourses.length} formations disponibles
              </h2>

              {filteredCourses.length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center">
                  <p className="text-lg text-gray-600">Aucune formation ne correspond à vos critères.</p>
                  <button
                    onClick={resetFilters}
                    className="mt-4 text-primary font-medium hover:underline"
                  >
                    Réinitialiser les filtres
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {filteredCourses.map((course, index) => (
                    <motion.div
                      key={course.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="card group"
                    >
                      <div className="relative overflow-hidden">
                        <img
                          src={course.image}
                          alt={course.title}
                          width="400"
                          height="192"
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 right-3 bg-white py-1 px-3 rounded-full text-xs font-medium text-primary">
                          {course.format}
                        </div>
                      </div>

                      <div className="p-6">
                        {course.badges && course.badges.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {course.badges.map(badge => (
                              <span
                                key={badge}
                                className="inline-block py-1 px-3 rounded-full text-xs font-medium bg-accent/10 text-accent"
                              >
                                {badge}
                              </span>
                            ))}
                          </div>
                        )}
                        <h3 className="text-xl font-semibold mb-3">{course.title}</h3>
                        <p className="text-gray-600 mb-6">{course.description}</p>

                        <div className="flex flex-wrap gap-4 mb-6">
                          <div className="flex items-center text-sm text-gray-500">
                            <BarChart className="h-4 w-4 mr-1" />
                            <span>{course.level}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock className="h-4 w-4 mr-1" />
                            <span>{course.duration}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <Users className="h-4 w-4 mr-1" />
                            <span>{course.participants}</span>
                          </div>
                        </div>

                        {course.durationOptions.filter(option => option !== course.duration).length > 0 && (
                          <p className="text-xs text-gray-500 mb-6 -mt-2">
                            {course.durationOptions.filter(option => option !== course.duration).length > 1 ? 'Autres formats : ' : 'Autre format : '}
                            {course.durationOptions.filter(option => option !== course.duration).join(', ')}
                          </p>
                        )}

                        <div className="mb-6">
                          <h4 className="font-medium mb-3">Bénéfices clés :</h4>
                          <ul className="space-y-2">
                            {course.benefits.map((benefit, i) => (
                              <li key={i} className="flex items-start">
                                <CheckCircle className="h-5 w-5 text-primary shrink-0 mr-2 mt-0.5" />
                                <span className="text-sm">{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="flex flex-wrap items-center justify-between">
                          <div className="font-medium mb-3">{course.price}</div>
                          <Link to={`/formationIA/${course.slug}/`} className="btn-primary mt-2 sm:mt-0">
                            Voir le détail de la formation
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Customized Training CTA */}
      <section className="bg-primary text-white py-16">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-lg mb-6">Formation sur mesure</h2>
              <p className="text-lg mb-6">
                Vous ne trouvez pas la formation qui correspond parfaitement à vos besoins ?
                Nous créons des programmes personnalisés adaptés aux spécificités de votre
                entreprise et à vos objectifs.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-accent shrink-0 mr-3 mt-0.5" />
                  <span>Analyse approfondie de vos besoins en compétences IA</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-accent shrink-0 mr-3 mt-0.5" />
                  <span>Conception de modules adaptés à votre secteur d'activité</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-accent shrink-0 mr-3 mt-0.5" />
                  <span>Flexibilité des formats (présentiel, distanciel, hybride)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-accent shrink-0 mr-3 mt-0.5" />
                  <span>Suivi personnalisé et mesure des résultats</span>
                </li>
              </ul>
              <Link to="/contact/" className="btn-accent">
                Discuter d'une formation sur mesure
              </Link>
            </div>
            <div className="relative">
              <img
                src="/surmesure.webp"
                alt="Formation IA sur mesure en entreprise"
                width="800"
                height="533"
                className="rounded-xl shadow-xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-accent p-6 rounded-lg shadow-xl">
                <p className="text-2xl font-bold">98%</p>
                <p className="text-sm">Taux de satisfaction</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FormationsPage;